import React from 'react';
import { formatDate, formatBusinessMonth } from '../../utils/dateFormatter';
import { TEXT, BDC_ROWS, VERSUS, priceVersusText } from '../../config/listingPerformanceLabels';
import { Figure, TrendArrow, VerdictChip, averageKind, ReadingNotes } from './parts';

/**
 * «أداء الوحدات» — Booking.com, one section per property (P-0001 + P-0002 share one today): the weekly
 * read against the competitive set, then the latest month. Booking.com lags about a week, so the picked
 * week falls back to its newest published week — and the page says so (plan §3a F1).
 */

const rowLabel = (m) => (m.key === 'unitBookedNightRate' ? `${BDC_ROWS.unitBookedNightRate.label} ${m.unitCode}` : BDC_ROWS[m.key]?.label || m.key);

const Versus = ({ m }) => {
  if (!m.versus) return null;
  if (m.key === 'bookedNightRate') return <span className="lp-versus">{priceVersusText(m.versus)}</span>;
  const v = VERSUS[m.versus];
  return v ? <span className={`lp-versus lp-versus--${v.tone}`}>{v.label}</span> : null;
};

/**
 * Ours on the right, theirs on the left, one line between them: this period (tinted, its arrow beside it) ·
 * the prior period · the 4-week average | the competitive set's figure with the comparison under it.
 */
const ReadTable = ({ metrics, ownerView, current, prior, showAverage }) => (
  <div className="lp-scroll">
    <table className="lp-table lp-bdc__table">
      <thead>
        <tr>
          <th scope="col">{TEXT.metric}</th>
          <th scope="col" className="lp-now-head">{current}</th>
          <th scope="col">{prior}</th>
          {showAverage && <th scope="col">{TEXT.fourWeekAvg}</th>}
          {!ownerView && <th scope="col" className="lp-group-start">{TEXT.competitors}</th>}
        </tr>
      </thead>
      <tbody>
        {metrics.map(m => {
          const kind = BDC_ROWS[m.key]?.kind || 'count';
          const hasComp = m.competitors !== null && m.competitors !== undefined;
          return (
            <tr key={`${m.key}-${m.unitCode || ''}`}>
              <th scope="row">{rowLabel(m)}</th>
              <td data-label={current} className="lp-detail__now">
                <span className="lp-cell"><Figure value={m.current} kind={kind} /><TrendArrow trend={m.trend} /></span>
              </td>
              <td data-label={prior}><Figure value={m.prior} kind={kind} /></td>
              {showAverage && <td data-label={TEXT.fourWeekAvg}><Figure value={m.average} kind={averageKind(kind, m.average)} /></td>}
              {!ownerView && (
                <td data-label={TEXT.competitors} className="lp-group-start lp-comp">
                  {hasComp && <Figure value={m.competitors} kind={averageKind(kind, m.competitors)} />}
                  <Versus m={m} />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const BookingcomSection = ({ sections = [], ownerView }) => {
  if (!sections.length) return <p className="lp-empty">{TEXT.noBookingcom}</p>;
  return sections.map(s => (
    <section key={s.otaPropertyId || s.unitCodes.join('-')} className="lp-bdc">
      <div className="lp-section-head">
        <div>
          <h3 className="lp-section-title">{TEXT.property} {s.otaPropertyId || ''} — {s.unitCodes.join('، ')}</h3>
          <p className="lp-muted">{TEXT.bdcWeekly}</p>
        </div>
        {!ownerView && <VerdictChip code={s.weekly?.verdict} platform="bookingcom" />}
      </div>
      {s.weekly?.fellBack && <p className="lp-banner" role="status">{TEXT.bdcFellBack}</p>}
      {s.weekly?.weekEnd ? (
        <ReadTable
          metrics={s.weekly.metrics}
          ownerView={ownerView}
          current={`${TEXT.thisWeek} (${formatDate(s.weekly.weekEnd, { year: undefined, month: 'short' })})`}
          prior={TEXT.priorWeek}
          showAverage
        />
      ) : <p className="lp-empty">{TEXT.noWeeks}</p>}
      {!ownerView && <ReadingNotes notes={s.weekly?.notes} readOn={s.weekly?.readOn} />}

      {s.monthly && (
        <>
          <div className="lp-section-head lp-section-head--sub">
            <p className="lp-muted">{TEXT.bdcMonthly}</p>
            {!ownerView && <VerdictChip code={s.monthly.verdict} platform="bookingcom" />}
          </div>
          <ReadTable
            metrics={s.monthly.metrics}
            ownerView={ownerView}
            current={`${TEXT.thisMonth} (${formatBusinessMonth(s.monthly.monthEnd.slice(0, 7))})`}
            prior={TEXT.priorMonth}
          />
          {!ownerView && <ReadingNotes notes={s.monthly.notes} readOn={s.monthly.readOn} />}
        </>
      )}
    </section>
  ));
};

export default BookingcomSection;
